import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider.js';
import { LoginPage } from './auth/LoginPage.js';
import { OrgProvider } from './app/OrgContext.js';
import { AppShell } from './app/AppShell.js';
import { Home } from './pages/Home.js';
import { Campaigns } from './pages/Campaigns.js';
import { Results } from './pages/Results.js';
import { Bookings } from './pages/Bookings.js';
import { CopyEngine } from './pages/CopyEngine.js';
import { Launch } from './pages/Launch.js';
import { Library } from './pages/Library.js';
import { Editor } from './pages/Editor.js';
import { Studio } from './pages/Studio.js';
import { Connections } from './pages/Connections.js';
import { Activity } from './pages/Activity.js';
import { Settings } from './pages/Settings.js';
import { SettingsLayout } from './pages/SettingsLayout.js';
import { StatutoryFigures } from './pages/Facts.js';
import { AcceptInvite } from './pages/AcceptInvite.js';
import { ToastProvider } from './ui/Toast.js';
import { C } from './lib/theme.js';

/** Redirect a retired path to its new home, preserving the query string. */
function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function Splash() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: C.muted,
        font: `500 12px 'IBM Plex Mono',monospace`,
        letterSpacing: '.12em',
      }}
    >
      LOADING…
    </div>
  );
}

function Gate() {
  const { session, loading } = useAuth();
  if (loading) return <Splash />;
  if (!session) return <LoginPage />;
  return (
    <OrgProvider>
      <ToastProvider>
        <Routes>
          <Route element={<AppShell />}>
            <Route path="/" element={<Home />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/results" element={<Results />} />
            <Route path="/bookings" element={<Bookings />} />
            {/* Studio — three self-contained tools, tabs = nested routes. */}
            <Route path="/studio" element={<Studio />}>
              <Route index element={<Navigate to="/studio/copy" replace />} />
              <Route path="create" element={<Editor />} />
              <Route path="copy" element={<CopyEngine />} />
              <Route path="library" element={<Library />} />
              {/* Retired sub-routes → new homes (preserve query, e.g. ?creative=). */}
              <Route path="compose" element={<LegacyRedirect to="/studio/create" />} />
              <Route path="posts" element={<LegacyRedirect to="/studio/library" />} />
            </Route>
            {/* Legacy paths → Studio tools (preserve query, e.g. ?creative=). */}
            <Route path="/media" element={<LegacyRedirect to="/studio/library?view=media" />} />
            <Route path="/copy" element={<LegacyRedirect to="/studio/copy" />} />
            <Route path="/editor" element={<LegacyRedirect to="/studio/create" />} />
            <Route path="/ads" element={<LegacyRedirect to="/studio/library" />} />
            <Route path="/launch" element={<Launch />} />
            {/* Settings hub: General + Connections + Activity as tabs */}
            <Route path="/settings" element={<SettingsLayout />}>
              <Route index element={<Settings />} />
              <Route path="connections" element={<Connections />} />
              <Route path="figures" element={<StatutoryFigures />} />
              <Route path="activity" element={<Activity />} />
            </Route>
            <Route path="/connections" element={<LegacyRedirect to="/settings/connections" />} />
            <Route path="/activity" element={<LegacyRedirect to="/settings/activity" />} />
          </Route>
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ToastProvider>
    </OrgProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
